import { toast } from 'sonner';
import React, { useState, useEffect } from 'react';
import { useCustomer } from '../hooks/useCustomer';
import { useCustomFields } from '../hooks/useCustomFields';
import { useFacility } from '../context/FacilityContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Search, Filter, User, Mail, Phone, MapPin, Plus,
  Upload, Download, X, CreditCard,
} from 'lucide-react';
import apiClient from '../utils/apiClient';
import customersService from '../services/customersService';
import { toLastFirst, byLastName } from '../utils/customerName';
import { exportRowsToCsv } from '../utils/csvExport';
import ListPager, { usePagedRows } from '../components/ListPager';
import HelpLink from '../components/HelpLink';

const CustomerSearch = () => {
  const location = useLocation();
  const fromState = { from: location.pathname + location.search };
  const { getCustomers, loading: customersLoading } = useCustomer();
  const { fields: customFields, fetchFields, loading: fieldsLoading } = useCustomFields();
  const { facilityId } = useFacility();
  const navigate = useNavigate();

  // Bulk selection -> archive. Server guards still apply per customer
  // (active rentals / unpaid invoices 400) — failures surface in the toast.
  const [selected, setSelected] = useState(new Set());
  const toggleSel = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const bulkArchive = async () => {
    const ids = [...selected];
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Archive ${ids.length} selected customer(s)? Customers with active rentals or open invoices are skipped by the server.`)) return;
    let ok = 0; const blocked = [];
    for (const id of ids) {
      try {
        await customersService.archive(facilityId, id);
        ok += 1;
      } catch (e) {
        const c = customers.find((x) => x._id === id);
        blocked.push(c?.full_name || id);
      }
    }
    setSelected(new Set());
    performSearch();
    if (blocked.length) {
      toast.warning(`Archived ${ok}. Blocked: ${blocked.slice(0, 5).join(', ')}${blocked.length > 5 ? '…' : ''}`);
    } else {
      toast.success(`Archived ${ok} customer(s).`);
    }
  };

  const exportCsv = () => exportRowsToCsv(
    `customers-${new Date().toISOString().slice(0, 10)}`,
    sortedCustomers,
    [
      { key: 'customer_number', label: 'Customer #' },
      { key: 'full_name', label: 'Name' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'address', label: 'Address' },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State' },
      { key: 'zip_code', label: 'ZIP' },
      { key: 'lifecycle', label: 'Lifecycle' },
      { key: 'is_archived', label: 'Archived' },
    ],
  );

  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState('');
  // Migration mode: relaxes the per-row PII requirement (email/phone/
  // address blank is OK). Used when bringing tenants over from an old
  // PMS where contact details are partial — operator backfills later.
  const [migrationMode, setMigrationMode] = useState(false);

  const downloadSample = async () => {
    try {
      const res = await apiClient.get(
        `/api/facilities/${facilityId}/customers/import/sample`,
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'customer_import_template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setImportError('Could not download the sample template');
    }
  };

  const _runImport = async (mode) => {
    if (!importFile) {
      setImportError('Choose a CSV file first');
      return;
    }
    setImporting(true);
    setImportError('');
    if (mode === 'dry-run') setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const qs = `mode=${mode}${migrationMode ? '&migration=true' : ''}`;
      const res = await apiClient.post(
        `/api/facilities/${facilityId}/customers/import?${qs}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setImportResult(res.data);
      if (mode === 'commit') performSearch();
    } catch (e) {
      setImportError(
        e.response?.data?.detail || 'Import failed. Check the file format.',
      );
    } finally {
      setImporting(false);
    }
  };
  // Preview the import first (dry-run, no DB writes). Then operator
  // confirms to commit. This is the two-step pattern from the docs:
  // "see what would happen, then approve."
  const previewImport = () => _runImport('dry-run');
  const commitImport = () => _runImport('commit');

  const [customers, setCustomers] = useState([]);
  // Must come AFTER the customers state: a `const` read before its
  // declaration is a temporal-dead-zone ReferenceError that crashes the
  // whole page to the error boundary (live incident 2026-07-06).
  const sortedCustomers = [...customers].sort(byLastName);
  const pager = usePagedRows(sortedCustomers);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    property_type: '',
    is_military: '',
    has_boats: false,
    has_vehicles: false,
    has_trailers: false,
    has_rvs: false,
    custom_fields: {}
  });
  const [showFilters, setShowFilters] = useState(false);
  // ESS-parity status filter. Defaults to "active" so the operator sees
  // currently-renting customers first. Persisted to localStorage so the
  // chosen view survives page reloads.
  const [statusFilter, setStatusFilter] = useState(() => {
    try { return localStorage.getItem('customers.statusFilter') || 'active'; }
    catch { return 'active'; }
  });

  useEffect(() => { fetchFields(); }, [fetchFields]);

  useEffect(() => {
    if (!facilityId) return;
    const timer = setTimeout(() => { performSearch(); }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, searchQuery, filters, statusFilter]);

  const performSearch = async () => {
    try {
      const apiFilters = {
        search: searchQuery,
        ...filters,
        // Archived visibility now rides the status dropdown alone: the
        // backend surfaces archived docs when status === "archived".
        status: statusFilter,
        is_military: filters.is_military === '' ? undefined : filters.is_military === 'true'
      };
      if (!filters.has_boats) delete apiFilters.has_boats;
      if (!filters.has_vehicles) delete apiFilters.has_vehicles;
      if (!filters.has_trailers) delete apiFilters.has_trailers;
      if (!filters.has_rvs) delete apiFilters.has_rvs;
      const results = await getCustomers(apiFilters);
      setCustomers(results);
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const handleArchive = async (e, customer) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (customer.is_archived) {
        await customersService.unarchive(facilityId, customer._id);
      } else {
        await customersService.archive(facilityId, customer._id);
      }
      performSearch();
    } catch (err) {
      // Archive is the FINAL step — balances/credits are handled earlier at the
      // rental move-out. A block here (active rental / unpaid invoices) is a
      // hard stop; surface the message.
      // eslint-disable-next-line no-alert
      toast.error(err?.response?.data?.detail || 'Failed to update customer.');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleCustomFieldFilter = (fieldId, value) => {
    setFilters(prev => {
      const newCustom = { ...prev.custom_fields };
      if (value === '') { delete newCustom[fieldId]; } else { newCustom[fieldId] = value; }
      return { ...prev, custom_fields: newCustom };
    });
  };

  const inputClass = "w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all";

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Customers <HelpLink slug="move-in" /></h1>
          <p className="text-[13px] text-slate-400 mt-0.5">{customers.length} customers</p>
        </div>
        {/* Compact on phones so all three fit ONE line (owner, 2026-08-10);
            full size from sm: up. */}
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
          <button
            onClick={exportCsv}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] sm:px-3.5 sm:py-2 sm:text-[13px] rounded-lg bg-blue-600 font-medium text-white hover:bg-blue-700 transition-colors"
          >
            <Download className="h-3.5 w-3.5" /> Export CSV
          </button>
          <button
            onClick={() => {
              setShowImport(true);
              setImportResult(null);
              setImportError('');
              setImportFile(null);
              setMigrationMode(false);
            }}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] sm:px-3.5 sm:py-2 sm:text-[13px] rounded-lg bg-blue-600 font-medium text-white hover:bg-blue-700 transition-colors"
          >
            <Upload className="h-3.5 w-3.5" /> Bulk Import
          </button>
          <Link to="/customers/new" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] sm:px-3.5 sm:py-2 sm:text-[13px] rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors">
            <Plus className="h-3.5 w-3.5" /> Add Customer
          </Link>
        </div>
      </div>

      {showImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 backdrop-blur-sm p-4">
          <div className="w-full max-w-lg max-h-[90vh] rounded-2xl bg-white shadow-xl flex flex-col">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5 shrink-0">
              <h2 className="text-[15px] font-semibold text-slate-900">
                Bulk import customers
              </h2>
              <button
                onClick={() => setShowImport(false)}
                className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5 space-y-4 flex-1 overflow-y-auto min-h-0">
              <div className="rounded-lg bg-indigo-50/60 border border-indigo-100 px-4 py-3">
                <p className="text-[13px] text-slate-700 font-medium mb-1">
                  Step 1 — download the template
                </p>
                <p className="text-[12px] text-slate-500 mb-2">
                  Fill it in Excel/Sheets. Required columns:{' '}
                  <span className="font-medium text-slate-700">
                    full_name, email, phone, address, city, state,
                    zip_code
                  </span>
                  . Optional: middle_name, date_of_birth,
                  payment_method (cash/check/card/ach), notes. Rent-roll
                  columns — add{' '}
                  <span className="font-medium text-slate-700">
                    unit_type, unit_number, monthly_rate
                  </span>{' '}
                  and the import also creates the unit + an active
                  rental. The template includes one example row.
                </p>
                <button
                  onClick={downloadSample}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-indigo-200 text-[12px] font-semibold text-indigo-700 hover:bg-indigo-50 transition-colors"
                >
                  <Download className="h-3.5 w-3.5" /> Download sample CSV
                </button>
              </div>

              <div>
                <p className="text-[13px] text-slate-700 font-medium mb-1.5">
                  Step 2 — upload your filled CSV
                </p>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => {
                    setImportFile(e.target.files?.[0] || null);
                    setImportResult(null);
                    setImportError('');
                  }}
                  className="block w-full text-[13px] text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-[12px] file:font-medium file:text-slate-700 hover:file:bg-slate-200"
                />
                {importFile && (
                  <p className="mt-1.5 text-[11px] text-slate-500">
                    Selected: <span className="font-medium text-slate-700">{importFile.name}</span> ({Math.round((importFile.size || 0) / 1024)} KB)
                  </p>
                )}
              </div>

              <div className="rounded-lg border border-amber-200/80 bg-amber-50/40 p-3">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={migrationMode}
                    onChange={(e) => {
                      setMigrationMode(e.target.checked);
                      setImportResult(null);
                      setImportError('');
                    }}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="text-[13px] font-medium text-amber-900">
                      Migration mode (use for acquisitions / old-system imports)
                    </div>
                    <p className="text-[11px] text-amber-800 mt-0.5">
                      Only <span className="font-semibold">full_name</span> is required. Blank email / phone / address are filled with placeholders so the rest of the row (unit, rent, balance) still lands. Backfill real contact info from the Customers list later.
                    </p>
                  </div>
                </label>
              </div>

              {importError && (
                <div className="rounded-lg bg-rose-50 border border-rose-200/80 px-3 py-2.5 text-[13px] text-rose-700">
                  {importError}
                </div>
              )}
              {importResult && (
                <div
                  className={`rounded-lg px-3 py-2.5 text-[13px] border ${
                    importResult.commit
                      ? 'bg-emerald-50 border-emerald-200/80 text-emerald-800'
                      : 'bg-amber-50 border-amber-200/80 text-amber-900'
                  }`}
                >
                  <p className="font-medium">{importResult.message}</p>
                  {/* Stats grid — show what would happen / did happen. */}
                  <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[12px]">
                    <span className="text-slate-600">Customers:</span>
                    <span className="text-slate-900 font-medium">
                      {importResult.customers_created ?? importResult.customers_to_create ?? 0}
                    </span>
                    <span className="text-slate-600">Rentals:</span>
                    <span className="text-slate-900 font-medium">
                      {importResult.rentals_created ?? importResult.rentals_to_create ?? 0}
                    </span>
                    <span className="text-slate-600">Units marked occupied:</span>
                    <span className="text-slate-900 font-medium">
                      {importResult.units_marked_occupied ?? importResult.units_to_mark_occupied ?? 0}
                    </span>
                    <span className="text-slate-600">Skipped rows:</span>
                    <span className="text-slate-900 font-medium">
                      {importResult.skipped_count ?? 0}
                    </span>
                  </div>
                  {importResult.skipped_count > 0 && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-[12px] text-slate-700 hover:text-slate-900">
                        Show skipped row details ({importResult.skipped_count})
                      </summary>
                      <ul className="mt-1.5 text-[12px] text-slate-600 list-disc list-inside max-h-32 overflow-y-auto">
                        {importResult.skipped_detail.map((s, i) => (
                          <li key={i}>
                            Row {s.row}
                            {s.field && (
                              <span className="text-slate-500"> ({s.field})</span>
                            )}
                            : {s.reason}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                  {importResult.warnings_count > 0 && (
                    <details className="mt-2" open>
                      <summary className="cursor-pointer text-[12px] text-amber-800 hover:text-amber-900">
                        Show warnings ({importResult.warnings_count}) — these rows imported with adjusted values
                      </summary>
                      <ul className="mt-1.5 text-[12px] text-amber-800 list-disc list-inside max-h-32 overflow-y-auto">
                        {importResult.warnings_detail.map((s, i) => (
                          <li key={i}>
                            Row {s.row}
                            {s.field && (
                              <span className="text-amber-700"> ({s.field})</span>
                            )}
                            : {s.reason}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3.5 shrink-0 bg-white rounded-b-2xl">
              <button
                onClick={() => setShowImport(false)}
                className="px-4 py-2 rounded-lg border border-slate-300 bg-slate-200 text-[13px] font-medium text-slate-600 hover:bg-slate-300"
              >
                Close
              </button>
              {/* Two-step pattern: preview first, then commit. The commit
                  button only appears after a successful dry-run. */}
              {!importResult?.commit && (
                <button
                  onClick={previewImport}
                  disabled={importing || !importFile}
                  className="px-4 py-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 text-[13px] font-semibold hover:bg-blue-100 disabled:opacity-50 transition-colors"
                >
                  {importing && importResult === null ? 'Previewing…' : 'Preview import (dry-run)'}
                </button>
              )}
              {importResult && !importResult.commit && (
                <button
                  onClick={commitImport}
                  disabled={importing || (importResult.customers_to_create ?? 0) === 0}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {importing ? 'Importing…' : 'Confirm and import'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mb-4 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            className="w-full pl-9 pr-10 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            placeholder="Search by name, email, phone, or customer number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md transition-colors ${showFilters ? 'text-blue-600 bg-blue-50' : 'text-slate-400 hover:text-slate-600'}`}
          >
            <Filter className="h-4 w-4" />
          </button>
        </div>
        {/* ESS-parity status dropdown — defaults to Active so the operator
            opens the page on currently-renting customers, not the full
            historical list. Persisted across reloads. */}
        <select
          value={statusFilter}
          onChange={(e) => {
            const v = e.target.value;
            setStatusFilter(v);
            try { localStorage.setItem('customers.statusFilter', v); } catch {}
          }}
          className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-[13px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all sm:w-56"
          title="Filter customers by status"
        >
          <option value="active">Active</option>
          <option value="current">Current</option>
          <option value="recurring">Recurring (autopay)</option>
          <option value="non_recurring">Non-Recurring</option>
          <option value="outstanding">Outstanding</option>
          <option value="past_due">Past Due</option>
          <option value="late">Late</option>
          <option value="locked_out">Locked Out</option>
          <option value="auction_set">Auction Date Set</option>
          <option value="waiting_list">Waiting List</option>
          <option value="late_fee_exempt">Late Fee Exempt</option>
          <option value="tax_exempt">Tax Exempt</option>
          <option value="active_tpp">Active Tenant Protection Plans</option>
          <option value="tpp_eligible">Tenant Protection Eligible</option>
          <option value="lockout_disabled">Automatic Lockout Disabled</option>
          <option value="prospects">Prospects</option>
          <option value="expired">Expired</option>
          <option value="marketing_pool">Marketing Pool</option>
          <option value="archived">Archived</option>
          <option value="ended">Ended (past tenants)</option>
          <option value="all">All</option>
        </select>
      </div>

      {showFilters && (
        <div className="rounded-xl border border-slate-200/80 bg-white p-4 mb-4">
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-3">Advanced Filters</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-[11px] font-medium text-slate-400 mb-1">Military Status</label>
              <select className={inputClass} value={filters.is_military} onChange={(e) => handleFilterChange('is_military', e.target.value)}>
                <option value="">Any</option>
                <option value="true">Active/Veteran</option>
                <option value="false">Non-Military</option>
              </select>
            </div>
            <div className="flex flex-col">
              <span className="text-[11px] font-medium text-slate-400 mb-1.5">Property Types</span>
              <div className="flex gap-3">
                {['Boats', 'Vehicles', 'Trailers', 'RVs'].map(type => (
                  <label key={type} className="inline-flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500/20"
                      checked={filters[`has_${type.toLowerCase()}`]}
                      onChange={(e) => handleFilterChange(`has_${type.toLowerCase()}`, e.target.checked)}
                    />
                    <span className="text-[13px] text-slate-600">{type}</span>
                  </label>
                ))}
              </div>
            </div>
            {fieldsLoading ? (
              <div className="text-[13px] text-slate-400">Loading fields...</div>
            ) : (
              customFields.filter(f => f.field_type === 'select' && f.is_searchable).map(field => (
                <div key={field.field_id}>
                  <label className="block text-[11px] font-medium text-slate-400 mb-1">{field.label}</label>
                  <select className={inputClass} value={filters.custom_fields[field.field_id] || ''} onChange={(e) => handleCustomFieldFilter(field.field_id, e.target.value)}>
                    <option value="">Any</option>
                    {field.options?.map(opt => (<option key={opt} value={opt}>{opt}</option>))}
                  </select>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
        {customersLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : customers.length === 0 ? (
          <div className="px-4 py-8 text-center text-[13px] text-slate-400">No customers found matching your criteria.</div>
        ) : (
          <>
          {selected.size > 0 && (
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-blue-200/80 bg-blue-50/60">
              <span className="text-[13px] font-medium text-slate-700">{selected.size} selected</span>
              <div className="flex items-center gap-2">
                <button onClick={bulkArchive}
                        className="rounded-lg bg-slate-700 text-white text-[12px] font-medium px-3.5 py-1.5 hover:bg-slate-800">
                  Archive selected
                </button>
                <button onClick={() => setSelected(new Set())}
                        className="text-[12px] text-slate-500 hover:text-slate-700 font-medium">
                  Clear
                </button>
              </div>
            </div>
          )}
          <div className="divide-y divide-slate-50">
            {/* Rows ordered alphabetically by last name (client-side, after
                fetch) — filters/search/status all still drive the result set;
                this only controls display order. */}
            {pager.pageRows.map((customer) => (
              <Link key={customer._id} to={`/customers/${customer._id}/edit`} state={fromState} className="block hover:bg-slate-50/50 transition-colors">
                <div className="px-4 py-3.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        className="rounded border-slate-300"
                        checked={selected.has(customer._id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => { e.stopPropagation(); toggleSel(customer._id); }}
                      />
                      <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center">
                        <User className="h-4 w-4 text-slate-400" />
                      </div>
                      <span className="text-[13px] font-medium text-blue-600">{toLastFirst(customer.full_name)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {customer.is_archived && (
                        <span className="px-1.5 py-0.5 text-[10px] rounded bg-slate-100 text-slate-500">Archived</span>
                      )}
                      {/* Lifecycle pill — first thing the operator sees on
                          prospects/expired rows so a non-customer row is
                          obvious without reading the derived_status. We do
                          NOT render a pill for lifecycle="customer" (the
                          default state); the derived_status pill carries
                          that signal already. */}
                      {customer.lifecycle === 'prospect' && (
                        <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-slate-100 text-slate-700 border border-slate-200">Prospect</span>
                      )}
                      {customer.lifecycle === 'expired' && (
                        <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-amber-50 text-amber-700 border border-amber-200">Expired</span>
                      )}
                      {customer.derived_status === 'Delinquent' && (
                        <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-rose-50 text-rose-700 border border-rose-200">Delinquent</span>
                      )}
                      {customer.derived_status === 'Active' && (
                        <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Active</span>
                      )}
                      {customer.gate_code && (
                        <span
                          className="px-2 py-0.5 text-[10px] font-mono rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200"
                          title="Gate code"
                        >
                          # {customer.gate_code}
                        </span>
                      )}
                      <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-slate-50 text-slate-500">{customer.customer_number}</span>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(`/payments/take?customer_id=${customer._id}`);
                        }}
                        title="Take payment"
                        className="text-slate-400 hover:text-blue-600 transition-colors"
                      >
                        <CreditCard className="h-3 w-3" />
                      </button>
                      <button
                        onClick={(e) => handleArchive(e, customer)}
                        className="text-[12px] text-slate-400 hover:text-slate-700 font-medium transition-colors"
                      >
                        {customer.is_archived ? 'Unarchive' : 'Archive'}
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-4 text-[13px] text-slate-500 ml-11">
                    {customer.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3 text-slate-300" />{customer.email}</span>}
                    {customer.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3 text-slate-300" />{customer.phone}</span>}
                    {customer.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3 text-slate-300" />{customer.city}</span>}
                  </div>
                  {(customer.boats?.length > 0 || customer.vehicles?.length > 0) && (
                    <div className="mt-2 flex gap-1.5 ml-11">
                      {customer.boats?.length > 0 && (
                        <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-blue-50 text-blue-600">{customer.boats.length} Boat(s)</span>
                      )}
                      {customer.vehicles?.length > 0 && (
                        <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-violet-50 text-violet-600">{customer.vehicles.length} Vehicle(s)</span>
                      )}
                    </div>
                  )}
                </div>
              </Link>
            ))}
            <ListPager {...pager} noun="customers" />
          </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerSearch;
